import { useEffect, useState } from "react"
import Input, { clearForm, FormState, required, submitForm, useTextInput } from "../../../../components/input"
import { api } from "../../../../env"
import LoadingButton from "../../../../components/LoadingButton"
import { useLocation, useNavigate } from "react-router-dom"
import { ConfirmButton } from "../../../../components/ConfirmButton"
import Title from "../../../../components/Title"
import { handleError } from "../../../../utils/errors/errorHandler"

const showApiError = (err, fallback) => {
    const message = err?.response?.data?.message?.message
    handleError(Array.isArray(message) ? message[0] : (message || fallback))
}

export default function TrackMeta({onChange, track}) {

    const navigate = useNavigate()
    const location = useLocation()
    const isManagerContext = location.pathname.startsWith('/manager')

    const handlePreview = () => {
        if (!displayTrack?.id) return
        if (isManagerContext) navigate(`/manager/tracks/${displayTrack.id}`)
        else navigate(`/admin/tracks/${displayTrack.id}/preview`)
    }

    const [state, setState] = useState(FormState.CREATE)
    const [displayTrack, setDisplayTrack] = useState(undefined)
    const [duplicating, setDuplicating] = useState(false)
    useEffect(() => {
        setDisplayTrack(track)
        if (track) setState(FormState.VIEW)
    }, [track])

    const trackForm = {
        name: useTextInput("Internal Name", "visibility_off", [required]),
        displayName: useTextInput("Display Name", "label", [required]),
        description: useTextInput("Description", "description", [])
    }
   
    const handleCreate = () => {
        const trackInfo = submitForm(trackForm)
        if(!trackInfo) return
        api.post('tracks', trackInfo)
            .then(res => {
                onChange(res.data)
            })
            .catch(err => {
                showApiError(err, "Could not create the track. Please try again.")
            })
    }

    const handleDuplicate = () => {
        setDuplicating(true)

        api.post('tracks', {
            name: `${track.name} (Clone)`,
            displayName: track.displayName,
            description: track.description
        })
            .then(res => {
                let duplicatedTrack = res.data
                let id = duplicatedTrack.id
                let chapters = track.chapters.map(chapter => {
                    return {
                        title: chapter.title,
                        theme: chapter.theme,
                        description: chapter.description,
                        activities: chapter.activities.map(activity => {
                            return {
                                title: activity.title,
                                description: activity.description
                            }
                        }),
                        sparkIds: chapter.sparks.map(spark => spark.sparkId)
                    }
                })
                api.post(`tracks/${id}/chapters/many`, {chapters})
                    .then(_ => {
                        setDuplicating(false)
                        navigate(`../${id}`)
                    })
                    .catch(_ => {
                        setDuplicating(false)
                        navigate(`../${id}`)
                    }) 
            })
            .catch(err => {
                setDuplicating(false)
                showApiError(err, "Could not duplicate the track. Please try again.")
            })
    }

    const handleUpdate = () => {
        const trackInfo = submitForm(trackForm)
        if(!trackInfo) return
        api.put(`tracks/${displayTrack.id}`, trackInfo)
            .then(res => {
                onChange(res.data)
                setState(FormState.VIEW)
            })
            .catch(err => {
                showApiError(err, "Could not save your changes. Please try again.")
            })
    }

    const handleDelete = () => {
        api.delete(`tracks/${displayTrack.id}`)
            .then(_ => navigate('..'))
            .catch(err => {
                showApiError(err, "Could not delete the track. Please try again.")
            })
    }

    const publish = () => {
        api.put(`tracks/${displayTrack.id}/publish`)
            .then(res => onChange(res.data))
            .catch(err => {
                showApiError(err, "Could not publish the track. Please try again.")
            })
    }

    const handleOnEdit = () => {
        trackForm.name._setValue(displayTrack.name)
        trackForm.displayName._setValue(displayTrack.displayName)
        trackForm.description._setValue(displayTrack.description)
        setState(FormState.UPDATE)
    }

    const handleOnCancel = () => {
        clearForm(trackForm)
        setState(FormState.VIEW)
    }

    return(<>
        <div className="flex space-between gap-xs center v-spacer-m">
            <Title>{state == FormState.CREATE ? 'Create' : 'Manage'} Track</Title>
            <div className="flex center gap-xs">
                {state != FormState.CREATE && <LoadingButton className={'outline'} label={'Duplicate'} loading={duplicating} onClick={handleDuplicate}/>}
                {state == FormState.VIEW && <>
                    <button className="outline with-icon leading" onClick={handlePreview}><span className="material-symbols-outlined scale-xs">visibility</span>Preview</button>
                    <ConfirmButton label={'Delete'} onClick={handleDelete}/>
                    <button className="outline" onClick={handleOnEdit}>Edit</button>
                </>}
                {displayTrack && displayTrack.status === 'Draft' && <ConfirmButton label={'Publish'} onClick={publish}/>}
            </div>
        </div>
        {state != FormState.VIEW && <div className="card flex columns gap-xs showing">
            <Input state={trackForm.name}/>
            <Input state={trackForm.displayName}/>
            <Input state={trackForm.description}/>
            <div className="flex gap-xs justify-end">
                {state == FormState.CREATE && <button className="solid-accent" onClick={handleCreate}>Create</button>}
                {state == FormState.UPDATE && <>
                    <button className="outline" onClick={handleOnCancel}>Cancel</button>
                    <button className="solid-accent" onClick={handleUpdate}>Save</button>
                </>}
            </div>
        </div>}
        {state == FormState.VIEW && <div className="card standard-padding flex columns center showing">
            <p className="section-title-m light">{displayTrack?.displayName} <span className="lighter">Track</span></p>
            <p className="card-title neutral v-spacer-l">&lt;{displayTrack?.name}&gt;</p>
            <p className="card-body text-centered">{displayTrack?.description}</p>
        </div>}
    </>)
}